// crm-web/frontend/src/components/analytics/HeroCards.jsx
// Container de las 4 hero cards. Hace fetch al endpoint /overview
// y decide que mostrar segun rol (admin vs agente).

import { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { analyticsAPI } from '../../services/api'
import HeroCard from './HeroCard'

// Thresholds client-side (espejo del backend config/analyticsThresholds.js).
// Duplicacion menor aceptada por simplicidad MVP.
const THRESHOLDS = {
  unclassifiedPct: { green: 10, amber: 25 },
  responseTimeMin: { green: 5, amber: 15 },
  unansweredChats: { green: 5, amber: 15 },
  classificationPct: { green: 80, amber: 50 },
}

// Helper: valor bajo -> verde, valor alto -> rojo (para metricas donde mas es peor)
function statusHighIsBad(value, { green, amber }) {
  if (value < green) return 'green'
  if (value <= amber) return 'amber'
  return 'red'
}

// Helper: valor alto -> verde, valor bajo -> rojo (para metricas donde mas es mejor)
function statusHighIsGood(value, { green, amber }) {
  if (value >= green) return 'green'
  if (value >= amber) return 'amber'
  return 'red'
}

export default function HeroCards({ range, refreshKey }) {
  const { user } = useAuth()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    analyticsAPI.getOverview(range)
      .then((res) => setData(res.data))
      .catch((err) => {
        console.error('[HeroCards] error:', err)
        setError('No se pudo cargar')
      })
      .finally(() => setLoading(false))
  }, [range, refreshKey])

  if (error) {
    return (
      <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
        {error}. <button onClick={() => setData(null)} className="underline">Reintentar</button>
      </div>
    )
  }

  // Vista Admin: 4 cards globales
  if (user?.is_admin) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <HeroCard
          title="Nuevos Prospectos"
          value={data?.nuevos_prospectos?.value ?? '-'}
          subtitle="en el período seleccionado"
          status="neutral"
          loading={loading}
        />
        <HeroCard
          title="Chats sin Clasificar"
          value={data ? `${data.pct_sin_clasificar.value}%` : '-'}
          subtitle={data?.pct_sin_clasificar?.detail}
          status={data ? statusHighIsBad(data.pct_sin_clasificar.value, THRESHOLDS.unclassifiedPct) : 'neutral'}
          loading={loading}
        />
        <HeroCard
          title="Tiempo de Respuesta"
          value={data ? `${data.tiempo_respuesta_p50_min.value}m` : '-'}
          subtitle="mediana (p50)"
          status={data ? statusHighIsBad(data.tiempo_respuesta_p50_min.value, THRESHOLDS.responseTimeMin) : 'neutral'}
          loading={loading}
        />
        <HeroCard
          title={`Chats sin resp. >${data?.chats_sin_respuesta?.threshold_min ?? 30}min`}
          value={data?.chats_sin_respuesta?.value ?? '-'}
          status={data ? statusHighIsBad(data.chats_sin_respuesta.value, THRESHOLDS.unansweredChats) : 'neutral'}
          loading={loading}
        />
      </div>
    )
  }

  // Vista Agente (no-admin): cards personales
  // NOTA: el backend ya retorna datos filtrados por agent (via scope del service).
  // El "% clasificados" es el complemento de pct_sin_clasificar.
  const myClassifiedPct = data ? 100 - data.pct_sin_clasificar.value : 0

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <HeroCard
        title="Mis Prospectos Nuevos"
        value={data?.nuevos_prospectos?.value ?? '-'}
        subtitle="en el período"
        status="neutral"
        loading={loading}
      />
      <HeroCard
        title="Mi % Clasificados"
        value={data ? `${Math.round(myClassifiedPct)}%` : '-'}
        subtitle={data?.pct_sin_clasificar?.detail}
        status={data ? statusHighIsGood(myClassifiedPct, THRESHOLDS.classificationPct) : 'neutral'}
        loading={loading}
      />
      <HeroCard
        title="Mi Tiempo de Respuesta"
        value={data ? `${data.tiempo_respuesta_p50_min.value}m` : '-'}
        subtitle="mediana personal"
        status={data ? statusHighIsBad(data.tiempo_respuesta_p50_min.value, THRESHOLDS.responseTimeMin) : 'neutral'}
        loading={loading}
      />
      <HeroCard
        title="Follow-ups"
        value="—"
        subtitle="(disponible en próxima entrega)"
        status="neutral"
        loading={loading}
      />
    </div>
  )
}
